import { AssignSupplementTypeModalQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import type { SupplementType } from 'modules/payroll_bundle/components/common/helper/worksheet'

type LegalEntities = AssignSupplementTypeModalQuery['companies']['legalEntities']

export default function buildSupplementType({
  legalEntities,
  id,
  legalEntityIdToTaxonomyId,
  units,
}: {
  legalEntities: LegalEntities
  id?: SupplementType['id']
  legalEntityIdToTaxonomyId: SupplementType['legalEntityIdToTaxonomyId']
  units: string
}): SupplementType {
  // Labels the button with the taxonomy for the first mapped legal entity.
  // The AssignSupplementTypeModal shows the legal entities in the order
  // that they are in the legalEntities collection, so the label to use
  // is the taxonomy for the first entity in the collection that has a
  // mapping.
  //
  // The spec says that the mapping for the default/main legal entity
  // should be used if it exists, otherwise the first mapping.
  // https://www.notion.so/factorialco/Supplements-importer-Multiple-types-36cb2d3fe3e442c099c48d7515c02f8d#d9aaa646035a42d0a4be76c56d7341d1
  // As long as the first legal entity in the collection, and so in the
  // AssignSupplementTypeModal, is the default/main one, this code will
  // meet the requirement.
  const firstMappedLegalEntity = legalEntities.find((le) => legalEntityIdToTaxonomyId[le.id])
  if (!firstMappedLegalEntity) {
    throw new Error('At least one legal entity must be mapped')
  }
  const taxonomyId = legalEntityIdToTaxonomyId[firstMappedLegalEntity?.id]
  // Mappings to "-- None --" ('0') are not counted
  const mappingsCount = Object.values(legalEntityIdToTaxonomyId).filter((t) => t !== '0').length
  return {
    id: id || Date.now().toString(),
    legalEntityIdToTaxonomyId: legalEntityIdToTaxonomyId,
    label:
      firstMappedLegalEntity.contractTaxonomies.find((t) => t.id === taxonomyId)?.name ||
      i18n.t('common.none'),
    units: units,
    mappingsCount,
  }
}
